<template>
  <div class="flex flex-col space-y-4">
    <div class="flex flex-wrap items-center justify-between">
      <label
        v-tooltip="{ content: subTitle, onShow: () => !!subTitle }"
        class="text-headline"
      >
        {{ label }}
        <span v-if="unit" class="text-sm text-gray-400">
          ({{ $t("COMMON.IN") }} {{ unitLabel }})
        </span>
      </label>
      <span
        v-if="modelValue.length && isSmartView"
        class="flex grow cursor-pointer items-center justify-end space-x-1"
        @click.stop.prevent="setRange(null)"
      >
        <span>{{ $t("COMMON.CLEAR") }}</span>
        <icon-x />
      </span>
    </div>
    <min-and-max-input
      v-if="modelValue"
      :max-options="{ 'data-maska': mask }"
      :min-options="{ 'data-maska': mask }"
      :min="modelValue[0] ?? null"
      :max="modelValue[1] || null"
      :min-allowed="minAllowed"
      :max-allowed="maxAllowed"
      @update:min="setRange"
      @update:max="setRange($event, true)"
    />
    <div
      v-if="!isSmartView"
      class="sticky bottom-0 flex h-11 items-center justify-center justify-evenly border-t bg-white px-4 py-0"
    >
      <a
        class="transform cursor-pointer text-sm font-medium text-primary active:scale-95"
        data-cy="clear-filter"
        @click.stop.prevent="setRange(null)"
      >
        {{ $t("COMMON.CLEAR_FILTER") }}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { Unit } from "@/enums/datetime";
</script>

<script setup lang="ts">
import MinAndMaxInput from "@/components/ui/inputs/MinAndMax.vue";

const emit = defineEmits<{
  "update:modelValue": [value: number[]];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: number[];
    unit?: Unit;
    label: string;
    mask?: string;
    subTitle?: string;
    isSmartView?: boolean;
    minAllowed?: number;
    maxAllowed?: number;
  }>(),
  { modelValue: () => [] as number[], mask: "####", isSmartView: true }
);

const { t } = useI18n();

const unitLabel = computed(() => {
  switch (props.unit) {
    case Unit.Dollar:
      return t("COMMON.DOLLAR_SIGN");
    case Unit.Minutes:
      return t("COMMON.MINUTE", 2);
    case Unit.Months:
      return t("COMMON.MONTHS");
    case Unit.Points:
      return t("COMMON.POINT", 2);
    default:
      return t("COMMON.HOUR", 2);
  }
});

const setRange = (value: number | null, settingMax = false) => {
  if (value === null) {
    if (settingMax) {
      emit("update:modelValue", [props.modelValue[0]]);
    } else {
      emit("update:modelValue", []);
    }
    return;
  }

  const [min, max] = props.modelValue;
  const newValue: (number | undefined)[] = [min, max];

  if (settingMax) {
    newValue[1] = value;
  } else {
    newValue[0] = value;
  }

  if (newValue.length === 1) {
    Number.isInteger(newValue[0])
      ? emit("update:modelValue", newValue as [number])
      : emit("update:modelValue", []);
    return;
  }
  if (newValue.length === 2) {
    if (newValue.every(Number.isInteger)) {
      emit("update:modelValue", newValue as [number, number]);
      return;
    }
    if (!Number.isInteger(newValue[0]) && !Number.isInteger(newValue[1])) {
      emit("update:modelValue", []);
      return;
    }
    if (!Number.isInteger(newValue[0])) {
      emit("update:modelValue", [0, newValue[1]] as [number, number]);
      return;
    }
    if (!Number.isInteger(newValue[1])) {
      emit("update:modelValue", [newValue[0]] as [number]);
      return;
    }
  }
};
</script>
